import Box from '@mui/material/Box';
import React, { useCallback, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useStyles from './styles';
import { ROUTES } from 'config/Nav';
import { useAuth, Callout, Loading } from '@elevator/ui';
import { PageHeader, SectionHeader } from 'molecules';
import ActionButtons from 'molecules/ActionButtons';
import withLayout from 'hocs/withLayout';

/**
 * Confirmation Component
 *
 * Displays a confirmation page after successful submission of an advertising campaign
 * or content submission. Shows the submission/campaign ID, optional new user account
 * creation message, and navigation links.
 *
 * Features:
 * - Authentication check with redirect if not authenticated
 * - Dynamic content based on type (advertising campaign vs content submission)
 * - Displays campaign/submission ID for user reference
 * - New user account creation callout with email verification reminder
 * - Content submission: `ActionButtons` (New Submission / View Submission), bottom-aligned
 * - Campaign: same layout with campaign copy, Campaign ID section, and Account / Home actions
 *
 * Query Parameters:
 * - campaignId: Campaign ID for advertising campaigns (string)
 * - submissionId: Submission ID for content submissions (string)
 * - isNewUser: Whether a new user account was created (string: "true" | "false")
 *
 * Used in:
 * - /campaigns/confirmation (advertising campaign confirmation)
 * - /submit/confirmation (content submission confirmation)
 *
 * Wrapped with {@link withLayout} (`sm` width + top margin below app bar).
 *
 * @returns Confirmation component with submission/campaign details and navigation
 */
const Confirmation = (): React.JSX.Element => {
  const { classes } = useStyles();
  const router = useRouter();
  const { currentUser, hydrateUser } = useAuth();

  // Extract and type query parameters safely
  const campaignId =
    typeof router.query.campaignId === 'string' ? router.query.campaignId : undefined;
  const submissionId =
    typeof router.query.submissionId === 'string' ? router.query.submissionId : undefined;
  const isNewUserParam = router.query.isNewUser;
  // Only show new user callout if explicitly "true" (not "false" or undefined)
  const isNewUser = isNewUserParam === 'true';

  // Determine if this is an advertising campaign confirmation
  // Use campaignId presence as primary indicator, fallback to route path
  const isAd =
    Boolean(campaignId) || router.pathname === ROUTES.ADVERTISE.CONFIRMATION.to;

  /**
   * Checks authentication and redirects to login if not authenticated
   */
  const checkAuth = useCallback(async () => {
    const updatedUser = await hydrateUser(false);
    if (!updatedUser) {
      router.push(ROUTES.AUTH.LOG_IN.to);
    }
  }, [hydrateUser, router]);

  useEffect(() => {
    if (!currentUser) {
      checkAuth();
    }
  }, [currentUser, checkAuth]);

  return (
    <Box className={classes.container} data-layout="confirmation" component="main">
      {currentUser ? (
        <>
          {isAd ? (
            <>
              <Box className={classes.mainContent}>
                <PageHeader title="Success!" divider={true} />
                <SectionHeader
                  title="Campaign Received"
                  titleVariant="h2"
                  titleComponent="h2"
                  divider={false}
                  description={
                    <Typography variant="body1" component="div">
                      Your ad campaign has successfully been submitted for review. We
                      process most campaign requests within 48 hours. Visit our{' '}
                      <MuiLink
                        component={Link}
                        href={ROUTES.FAQ.to}
                        color="secondary"
                        underline="hover"
                        fontWeight={600}
                      >
                        FAQ
                      </MuiLink>{' '}
                      page to learn more.
                    </Typography>
                  }
                />
                <SectionHeader
                  title="Campaign ID"
                  titleVariant="h6"
                  titleComponent="h6"
                  divider={false}
                  description={campaignId || 'N/A'}
                />
                {isNewUser && (
                  <Callout>
                    <Typography variant={'body1'} component={'span'}>
                      Your account has been created! Please note that email verification
                      is required if you ever need to reset your password.{' '}
                    </Typography>
                    <Typography color={'error'} variant={'subtitle1'} component={'span'}>
                      Please check your email.
                    </Typography>
                  </Callout>
                )}
              </Box>
              <Box className={classes.submissionActions}>
                <ActionButtons
                  cancel={{
                    label: 'ELEVATOR HOME',
                    onClick: () => {
                      void router.push(ROUTES.HOME.to);
                    },
                  }}
                  submit={{
                    label: 'Go to My Account',
                    onClick: () => {
                      void router.push(ROUTES.ACCOUNT.to);
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              <Box className={classes.mainContent}>
                <PageHeader title="Success!" divider={true} />
                <SectionHeader
                  title="Submission Received"
                  titleVariant="h2"
                  titleComponent="h2"
                  divider={false}
                  description={
                    <Typography variant="body1" component="div">
                      Your content has successfully been submitted for review. We review
                      most submissions within 72 hours but it may take up to 7 days. Visit
                      our{' '}
                      <MuiLink
                        component={Link}
                        href={ROUTES.FAQ.to}
                        color="secondary"
                        underline="hover"
                        fontWeight={600}
                      >
                        FAQ
                      </MuiLink>{' '}
                      page to learn more.
                    </Typography>
                  }
                />
                {isNewUser && (
                  <Callout>
                    <Typography variant={'body1'} component={'span'}>
                      Your account has been created! Please note that email verification
                      is required if you ever need to reset your password.{' '}
                    </Typography>
                    <Typography color={'error'} variant={'subtitle1'} component={'span'}>
                      Please check your email.
                    </Typography>
                  </Callout>
                )}
              </Box>
              <Box className={classes.submissionActions}>
                <ActionButtons
                  cancel={{
                    label: 'New Submission',
                    onClick: () => {
                      void router.push(ROUTES.SUBMIT.to);
                    },
                  }}
                  submit={{
                    label: 'View Submission',
                    onClick: () => {
                      if (submissionId) {
                        void router.push(
                          `${ROUTES.ACCOUNT.SUBMISSIONS.to}/${encodeURIComponent(submissionId)}`,
                        );
                      }
                    },
                    disabled: !submissionId,
                  }}
                />
              </Box>
            </>
          )}
        </>
      ) : (
        <Loading />
      )}
    </Box>
  );
};

export default withLayout(Confirmation, { contentWidth: 'sm', margin: true });
